import Icon from '../../../components/AppIcon';
import { useTranslation } from '../../../contexts/TranslationContext';
import type { Feature } from '../types';

interface FeaturesSectionProps {
  features: Feature[];
}

const FeaturesSection = ({ features }: FeaturesSectionProps) => {
  const { t, isLoading: translationsLoading } = useTranslation();
  
  const getTranslation = (key: string, fallback: string): string => {
    if (translationsLoading) return fallback;
    const translation = t(key);
    return translation === key ? fallback : translation;
  };

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
            <Icon name="Zap" size={18} className="text-primary" />
            <span className="text-sm font-medium text-primary">{getTranslation('landing.features.badge', 'Platform Features')}</span>
          </div>
          
          <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
            {getTranslation('landing.features.headline', 'Everything You Need to Engage & Grow')}
          </h2>
          
          <p className="text-lg text-muted-foreground">
            {getTranslation('landing.features.description', 'Discover powerful tools designed to amplify youth voices and create meaningful connections')}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <div
              key={feature.id}
              className="group relative bg-card border border-border rounded-xl p-6 hover:border-primary transition-smooth elevation-sm hover:elevation-md"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-smooth" />
              
              <div className="relative space-y-4">
                <div className="w-14 h-14 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-smooth">
                  <Icon
                    name={feature.icon}
                    size={28}
                    className="text-primary group-hover:text-primary-foreground transition-smooth"
                  />
                </div>

                <h3 className="text-xl font-semibold text-foreground group-hover:text-primary transition-smooth">
                  {feature.title}
                </h3>

                <p className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>

                <div className="flex items-center gap-2 text-primary opacity-0 group-hover:opacity-100 transition-smooth">
                  <span className="text-sm font-medium">Learn more</span>
                  <Icon name="ArrowRight" size={16} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;